import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import chunk from 'lodash/chunk';

import { Breadcrumbs, Search, SearchAltIcon, useDebounce } from '@utilities';
import { readSearchResults } from '@api/prompts';

import Answers from './components/Answers';
import Attributes from './components/Attributes';
import AttributeOptionsResults from './components/AttributeOptionResults';
import DateRangeOptions from './components/DateRangeOptions';
import PurchaseVolumeOptions from './components/PurchaseVolumeOptions';
import SelectedAnswers from './components/SelectedAnswers';

import styles from './_index.module.scss';

const Results = ({
  activePrompt,
  fastTrackId,
  onAnswerChange,
  sameLevelPrompts,
  searchPrompts,
  selectedAnswers,
  setSelectedAnswers,
  transformedAnswersMap,
}) => {
  const [activeAttribute, setActiveAttribute] = useState(null);
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsCustom, setResultsCustom] = useState([]);
  const [searchQuery, setSearchQuery, { isDebouncing, signal }] = useDebounce('');

  const searchRef = useRef(null);

  const promptKey = activePrompt?.promptKey;
  const activePromptAnswers = selectedAnswers?.[promptKey] || [];
  let attributes = activePrompt?.attributes || [];

  const relatedSameLevelPromptKeys = Array.isArray(sameLevelPrompts)
    ? (
        sameLevelPrompts?.find((group) => Array.isArray(group) && group.includes(promptKey)) || []
      ).filter((key) => key !== promptKey)
    : [];

  const activeSameLevelAttributes = useMemo(() => {
    if (relatedSameLevelPromptKeys.length === 0) {
      return [];
    }
    const relatedSameLevelKey = relatedSameLevelPromptKeys[0];
    const relatedSamePromptLevel = selectedAnswers?.[relatedSameLevelKey]?.[0]?.attributeId ?? null;
    if (!relatedSamePromptLevel) {
      return [];
    }

    return attributes.filter((attr) => attr.id === relatedSamePromptLevel);
  }, [attributes, relatedSameLevelPromptKeys, selectedAnswers]);

  if (activeSameLevelAttributes.length > 0) {
    attributes = activeSameLevelAttributes;
  }

  const relatedPromptKeys = searchPrompts
    ?.find((group) => group.includes(promptKey))
    ?.filter((key) => key !== promptKey);

  const activeHierarchySearchAnswers = useMemo(() => {
    if (!relatedPromptKeys?.length) return {};

    return Object.fromEntries(
      Object.entries(selectedAnswers || {})
        .filter(([key]) => relatedPromptKeys.includes(key))
        .map(([key, answers]) => [key, answers.map(({ attributeId, id }) => ({ attributeId, id }))])
    );
  }, [relatedPromptKeys, selectedAnswers]);

  useEffect(() => {
    setSearchQuery('');
    searchRef?.current?.abort();
    setActiveAttribute(null);
  }, [activePrompt]);

  useEffect(() => {
    if (!promptKey || (!activeAttribute?.id && !searchQuery)) return;

    searchRef?.current?.abort();
    const controller = new AbortController();
    searchRef.current = controller;

    setIsLoading(true);
    setIsCustomLoading(true);
    setResults([]);
    setResultsCustom([]);

    const customAttributes = attributes.filter((attr) => attr?.isCustom);
    const regularAttributes = attributes.filter((attr) => !attr?.isCustom);

    const fetchCustomSearchResults = async () => {
      const customChunks = chunk(
        customAttributes.map((a) => a.id),
        20
      );

      if (searchQuery) {
        try {
          const customSearches = customChunks?.map((ids) =>
            readSearchResults({
              attributeIds: ids,
              promptId: promptKey,
              searchAnswers: activeHierarchySearchAnswers,
              searchPrompts,
              searchQuery,
              signal: controller.signal,
            })
          );

          const customResultsRaw = await Promise.all(customSearches);
          const mergedCustom = customResultsRaw.flatMap((customResult) => customResult?.data || []);
          setResultsCustom(mergedCustom);
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Failed to fetch custom search results:', err);
          }
        } finally {
          if (!controller.signal.aborted) setIsCustomLoading(false);
        }
      }
    };

    const fetchSearchResults = async () => {
      try {
        const regularChunks = chunk(
          searchQuery && !activeAttribute?.id
            ? regularAttributes.map((a) => a.id)
            : [activeAttribute?.id],
          10
        );

        const regularSearches = regularChunks?.map((ids) =>
          readSearchResults({
            attributeIds: ids,
            ...(fastTrackId ? { fastTrackId } : {}),
            promptId: promptKey,
            searchAnswers: activeHierarchySearchAnswers,
            searchPrompts,
            searchQuery,
            signal: controller.signal,
          })
        );

        const regularResultsRaw = await Promise.all(regularSearches);
        const mergedRegular = regularResultsRaw.flatMap(
          (regularResult) => regularResult?.data || []
        );

        setResults(mergedRegular);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch search results:', err);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchSearchResults();
    fetchCustomSearchResults();

    return () => {
      searchRef.current?.abort();
    };
  }, [activeAttribute?.id, signal]);

  const combinedResults = useMemo(() => [...results, ...resultsCustom], [results, resultsCustom]);

  const isDatePrompt = promptKey === 'date_range' || promptKey === 'comparison_period';
  const isVolumePrompt = promptKey === 'purchase_volume' || promptKey === 'purchase_shift';

  let breadcrumbs = [
    {
      label: activePrompt?.name,
      onClick: () => {
        setActiveAttribute(null);
        setSearchQuery('');
      },
    },
  ];

  if (activeAttribute && activePrompt?.attributes?.length > 1) {
    breadcrumbs.push({ label: activeAttribute?.name });
  }

  const renderResults = () => {
    if (isDatePrompt)
      return (
        <DateRangeOptions
          activePrompt={activePrompt}
          activePromptAnswers={activePromptAnswers}
          onAnswerChange={onAnswerChange}
          promptKey={promptKey}
        />
      );

    if (isVolumePrompt)
      return (
        <PurchaseVolumeOptions
          activePrompt={activePrompt}
          activePromptAnswers={activePromptAnswers}
          onAnswerChange={onAnswerChange}
          promptKey={promptKey}
        />
      );

    return (
      <>
        <Search
          className={styles['prompt-result-search']}
          icon={<SearchAltIcon />}
          onChange={setSearchQuery}
          placeholder={`Filter ${activePrompt?.name}`}
          value={searchQuery}
        />
        <Breadcrumbs options={breadcrumbs} />
        {searchQuery ? (
          <AttributeOptionsResults
            activePrompt={activePrompt}
            activePromptAnswers={activePromptAnswers}
            isCustomLoading={isDebouncing || isCustomLoading}
            isLoading={isDebouncing || isLoading}
            onAnswerChange={onAnswerChange}
            results={combinedResults}
          />
        ) : (
          <div className={styles['prompt-results']}>
            {!activeAttribute ? (
              <Attributes
                activeAttribute={activeAttribute}
                attributes={attributes}
                setActiveAttribute={setActiveAttribute}
              />
            ) : (
              <Answers
                activePrompt={activePrompt}
                activePromptAnswers={activePromptAnswers}
                isLoading={isDebouncing || isLoading}
                onAnswerChange={onAnswerChange}
                results={combinedResults}
              />
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles['results']}>
      <div className={styles['results-header']}>
        <h3 className={styles['results-header-title']}>{activePrompt?.name}</h3>
        {transformedAnswersMap && !isDatePrompt && !isVolumePrompt && (
          <div className={styles['results-selected-answers']}>
            <SelectedAnswers
              activePrompt={activePrompt}
              setSelectedAnswers={setSelectedAnswers}
              transformedAnswersMap={transformedAnswersMap}
            />
          </div>
        )}
      </div>
      {renderResults()}
    </div>
  );
};

Results.propTypes = {
  activePrompt: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        isCustom: PropTypes.bool,
        name: PropTypes.string,
      })
    ),
    name: PropTypes.string,
    promptKey: PropTypes.string.isRequired,
  }).isRequired,
  fastTrackId: PropTypes.string,
  onAnswerChange: PropTypes.func,
  sameLevelPrompts: PropTypes.array,
  searchPrompts: PropTypes.array,
  selectedAnswers: PropTypes.object,
  setSelectedAnswers: PropTypes.func,
  transformedAnswersMap: PropTypes.object,
};

export default Results;
